import { Card, Link as DSLink, Heading } from "@icg360/design-system";

import { PublicHeader } from "components/public-header";
import { PageFooter } from "components/shared/page-footer";

import styles from "./privacy.module.scss";

const Privacy = () => {
  return (
    <>
      <PublicHeader hideNav />
      <div className={styles.privacyContainer}>
        <div className={styles.privacyCard}>
          <Card title="MySageSure Privacy Policy">
            <div className={styles.welcome}>
              <div>
                Welcome to MySageSure! When you use MySageSure, you are agreeing
                to our Privacy Policy. Please review this Privacy Policy
                carefully.
              </div>
              <div className={styles.revisedDate}>Last Revised: 07/01/2023</div>
            </div>
            <div className={styles.content}>
              <Heading size="sm" className={styles.sectionHeading}>
                I. Introduction:
              </Heading>
              <p>
                MySageSure is provided by SageSure Insurance Managers LLC
                (“SageSure,” “us,” or “we”). This Privacy Policy governs your
                access to and use of the MySageSure online portal and payment
                applications (“MySageSure”). By using MySageSure, you signify
                your agreement to this Privacy Policy and our Terms of Use. If
                you do not agree to this Privacy Policy and our{" "}
                <DSLink href="/terms-of-use/" target="_blank" rel="noopener">
                  Terms of Use
                </DSLink>
                , you may not use MySageSure. Please note that this Privacy
                Policy does not apply to use of any of our offerings or services
                other than MySageSure. Your use of any other of our offerings or
                services will be subject to an additional terms of use and
                privacy policy, as applicable.
              </p>
            </div>

            <div className={styles.content}>
              <Heading size="sm" className={styles.sectionHeading}>
                II. Privacy Policy:
              </Heading>
              <p>
                We respect your privacy and created this Privacy Policy to let
                you know what information we collect when you access and/or use
                our Websites and how we may use or disclose it.
              </p>
              <p>
                This PRIVACY NOTICE of SageSure Insurance Managers LLC
                (collectively, “SageSure,” “we,” “us,” or “our”) and applies to
                visitors, users, customers, consumers, and others (“consumers”
                or “you”). It explains how we collect, use, protect, and share
                your personal information and your rights under the privacy laws
                of your state, and other applicable privacy laws and
                regulations.
              </p>
            </div>

            <div className={styles.content}>
              <Heading size="sm" className={styles.sectionHeading}>
                Categories of Personal Information We Collect
              </Heading>
              <p>
                We collect the following categories of personal information from
                or about our consumers:
              </p>
              <ul className={styles.bulletList}>
                <li>
                  Identifiers and personal information categories (e.g., name,
                  date of birth, mailing address, phone number, email address,
                  internet protocol address, Social Security number, insurance
                  policy number, driver&apos;s license number, bank account
                  number and other payment information, financial history,
                  marital status).
                </li>
                <li>
                  Protected classification characteristics (e.g., age and
                  marital status).
                </li>
                <li>
                  Commercial information (e.g., records of personal property,
                  payment history, claims history, other payment or financial
                  information, products or services purchased, obtained, or
                  considered, or other purchasing or consuming histories or
                  tendencies).
                </li>
                <li>
                  Internet or other electronic network activity information
                  regarding your interaction with our website (e.g., browsing
                  history, cookies, search history, information on a
                  consumer&apos;s interaction with a website, application, or
                  advertisement).
                </li>
                <li>
                  <b>Sensitive Personal Information:</b> We collect the
                  following information, which is considered to be “sensitive”
                  personal information: social security number, driver&apos;s
                  license number, bank account number(s), and marital status.
                </li>
              </ul>
            </div>

            <div className={styles.content}>
              <Heading size="sm" className={styles.sectionHeading}>
                Sources of Information
              </Heading>
              <p>
                We obtain the categories of personal information listed above
                from the following categories of sources:
              </p>
              <ul className={styles.bulletList}>
                <li>
                  Directly from our consumers. For example, we collect the
                  information that you provide (or someone else who provides it
                  on your behalf) when you fill out online applications, or
                  other forms, and when you submit questions or comments online
                  or by email or by text messages.
                </li>
                <li>
                  Directly and indirectly from activity on our website. For
                  example, we collect information from submissions through our
                  website portal and website usage details collected
                  automatically through cookies and other similar tracking
                  technologies.
                </li>
                <li>
                  From consumer reporting agencies, public records and data
                  collection agencies. For example, we collect information
                  regarding your obligations with others, your creditworthiness,
                  loss history, and claims data from these sources.
                </li>
                <li>
                  From transactions with SageSure or other nonaffiliated
                  parties. For example, we collect information, such as your
                  name, address, availability of funds, insurance coverage,
                  transaction history, claims history and premiums, from these
                  sources.
                </li>
              </ul>
            </div>

            <div className={styles.content}>
              <Heading size="sm" className={styles.sectionHeading}>
                Automatic Collection of Personal Data
              </Heading>
              <p>
                A cookie is a file that a website can place on your
                computer&apos;s hard drive for record-keeping or other
                administrative purposes. We may use cookies to help personalize
                your experience or to market products and services to you. When
                you access or use MySageSure, we may automatically collect
                information about you, such as the type of computer you are
                using, its operating system, Internet Protocol (“IP”) address,
                access times, browser type and language, and the URL of the
                website you visited before navigating to MySageSure.
              </p>
              <p>
                If you access MySageSure via a mobile device, we may also
                collect your device make and model, mobile carrier, operating
                system version, language your device is set to, mobile web
                browser type and version, the country in which you are using
                your device, and analytics used to collect aggregate data on
                usage, downloads, frequency of use, retention, and territories
                and resources you access while using MySageSure. We do not ask
                you for, access, or track any location-based information, other
                than country-level information, from your mobile device at any
                time while using MySageSure.
              </p>
              <p>
                To collect these types of information, we use certain
                technologies, including those identified below:
              </p>
              <ul className={styles.bulletList}>
                <li>
                  Browser Cookies: Cookies are small data files stored on your
                  computer or mobile device by a website. Among other things,
                  cookies help us improve MySageSure and your experience, see
                  which areas and features of MySageSure are popular, and count
                  visits. In addition, when you view a video on MySageSure, we
                  may set a “flash cookie” (sometimes called a “flash object”)
                  on your computer or device. Most web browsers are set to
                  accept cookies by default. If you prefer, you can usually
                  choose to set your browser to remove or reject browser
                  cookies. Removing or rejecting browser cookies does not
                  necessarily affect third party flash cookies used in
                  connection with MySageSure. For more information about how to
                  delete or disable flash cookies, please visit{" "}
                  <DSLink
                    href="https://www.adobe.com/products/flashplayer/security"
                    target="_blank"
                    rel="noopener"
                  >
                    www.adobe.com/products/flashplayer/security
                  </DSLink>
                  . Note that if you choose to remove or reject cookies, this
                  could affect the availability and functionality of MySageSure.
                </li>
                <li>
                  Storage Cookies: Certain features of MySageSure may use
                  locally stored objects (storage cookies) to collect and store
                  information about your preferences and navigation to, from and
                  on MySageSure. Storage cookies are not managed by the same
                  browser settings as are used for browser cookies.
                </li>
                <li>
                  Web Beacons: MySageSure and emails received from us may
                  contain small electronic files known as web beacons (also
                  referred to as web bugs, clear gifs, pixel tags and
                  single-pixel gifs) that permit us, for example, to monitor
                  user behavior, deliver cookies, collect information, count
                  visits, understand usage and campaign effectiveness, to tell
                  if a recipient has opened and acted upon an email and
                  verifying system and server integrity.
                </li>
                <li>
                  Log Files: Log files record activity on MySageSure and enable
                  us to gather statistics about our users&apos; habits. These
                  entries help us determine, among other things, how many and
                  how often users have accessed or used MySageSure, which pages
                  of MySageSure they&apos;ve visited, and other similar data.
                </li>
              </ul>
            </div>

            <div className={styles.content}>
              <Heading size="sm" className={styles.sectionHeading}>
                Purposes and Use of Collected Personal Information
              </Heading>
              <p>
                We may use or disclose the personal information, including
                certain sensitive personal information, we collect for one or
                more of the following business purposes:
              </p>
              <ul className={styles.bulletList}>
                <li>
                  To fulfill or meet the reason for which the information is
                  provided.
                </li>
                <li>
                  To provide you with information, products or services that you
                  request from us.
                </li>
                <li>
                  To maintain or service consumer accounts and to provide
                  customer service.
                </li>
                <li>
                  To provide you with email alerts, text message alerts, event
                  registrations and other notices concerning our products or
                  services, or events or news, that may be of interest to you.
                </li>
                <li>
                  To carry out our obligations and enforce our rights arising
                  from any contracts entered into between you and us.
                </li>
                <li>
                  Improve, market or promote products and services to you.
                </li>
                <li>
                  Compile, synthesize, generate and analyze anonymous usage and
                  other aggregated information.
                </li>
                <li>
                  To improve user experience by making our website easier to use
                  and navigate.
                </li>
                <li>
                  To evaluate or conduct a business combination, such as a
                  merger or sale.
                </li>
                <li>
                  As necessary or appropriate to protect the rights, property or
                  safety of us, our clients or others.
                </li>
                <li>
                  To respond to law enforcement requests and as required by
                  applicable law, court order, or governmental regulations.
                </li>
                <li>
                  As described to you when collecting your personal information.
                </li>
              </ul>
              <p>
                We will not collect additional categories of personal
                information or use the personal information we collected for
                materially different, unrelated, or incompatible purposes
                without providing you notice.
              </p>
            </div>

            <div className={styles.content}>
              <Heading size="sm" className={styles.sectionHeading}>
                Sharing Personal Information
              </Heading>
              <p>
                We may disclose your personal information, including certain
                sensitive personal information, to a third party for business
                purposes, as described above. As permitted by applicable law, we
                may share the following categories of personal information with
                a third party for a business purpose:
              </p>
              <ul className={styles.bulletList}>
                <li>
                  Identifiers and personal information categories (e.g., name,
                  date of birth, mailing address, phone number, email address,
                  Internet Protocol address, Social Security number, insurance
                  policy number, bank account number and other payment
                  information, financial history, marital status)
                </li>
                <li>
                  Protected classification characteristics (e.g., age, marital
                  status)
                </li>
                <li>
                  Commercial information (e.g., records of personal property,
                  products or services purchased, obtained, or considered, or
                  other purchasing or consuming histories or tendencies)
                </li>
                <li>
                  Internet or other electronic network activity information
                  (e.g., browsing history, search history, information on a
                  consumer&apos;s interaction with a website, application, or
                  advertisement)
                </li>
                <li>
                  We may also disclose aggregated information about our users,
                  and information that does not identify any individual, without
                  restriction.
                </li>
              </ul>
              <br />
              <p>
                We may, as allowed by law, disclose your personal information,
                including certain sensitive personal information, for a business
                purpose to the following categories of third parties:
              </p>
              <ul className={styles.bulletList}>
                <li>
                  Third parties that use your personal information to process
                  payments, validate your credit and loss history, and provide
                  other similar services on our behalf.
                </li>
                <li>
                  Other insurance institutions, financial institutions,
                  reinsurers, insurance producers, or consumer reporting
                  agencies as needed in connection with any insurance
                  application, policy or claim involving you.
                </li>
                <li>
                  Third parties that conduct research, including actuarial or
                  underwriting studies.
                </li>
                <li>
                  Adjusters, appraisers, auditors, investigators and attorneys.
                </li>
                <li>
                  Third parties that may explore certain transactions with
                  SageSure, such as certain investments, loan transactions, and
                  mergers and acquisitions of portions or all of our business.
                </li>
                <li>
                  Third parties who perform business, professional or insurance
                  functions for SageSure including service providers, agents or
                  independent contractors.
                </li>
                <li>To our subsidiaries and affiliated companies.</li>
                <li>
                  We may disclose your personal information to law enforcement,
                  government agencies, and other related third parties, in order
                  to comply with the law, enforce our policies, or protect our
                  or others&apos; rights, property, or safety.
                </li>
              </ul>
              <br />
              <b>We do not sell any of your personal information.</b>
            </div>

            <div className={styles.content}>
              <Heading size="sm" className={styles.sectionHeading}>
                Third-Party Links
              </Heading>
              <p>
                Our website contains links to other sites. SageSure is not
                responsible for the privacy practices or content of such other
                sites. If you have any questions about how these other sites use
                your information, you should review their policies and contact
                them directly.
              </p>
              <p>
                Certain functions in MySageSure may be processed through or
                provided by third parties. Currently, if you request a payment
                to be processed through MySageSure, it will be handled by our
                vendor and subject to its privacy policy available{" "}
                <DSLink
                  href="https://www.oneinc.com/privacy#:~:text=One%20Inc%20is%20committed%20to,the%20information%20we%20collect%20online"
                  target="_blank"
                  rel="noopener"
                >
                  here
                </DSLink>
                .
              </p>
            </div>

            <div className={styles.content}>
              <Heading size="sm" className={styles.sectionHeading}>
                How SageSure Responds to Do-Not-Track Signals
              </Heading>
              <p>
                Certain state law requires commercial website owners and
                operators to disclose how their website responds to web browser
                “do not track” signals. If you have enabled your browser for
                that purpose, at this time, we do not honor “do not track”
                requests or change our services or information practices in any
                way as a result of receiving “do not track” requests from your
                browser.
              </p>
            </div>

            <div className={styles.content}>
              <Heading size="sm" className={styles.sectionHeading}>
                Collection and Use of Information From Children
              </Heading>
              <p>
                NOTICE TO CHILDREN UNDER THE AGE OF 13 AND THEIR PARENTS OR
                LEGAL GUARDIANS: SAGESURE/ICG360 DOES NOT KNOWINGLY COLLECT
                PERSONAL INFORMATION FROM CHILDREN UNDER THE AGE OF 13 AND
                CHILDREN UNDER THE AGE OF 13 ARE SPECIFICALLY PROHIBITED FROM
                SUBMITTING ANY PERSONAL INFORMATION ON THE WEBSITES. IF YOU ARE
                UNDER THE AGE OF 13, DO NOT SEND SAGESURE/ICG360 ANY PERSONAL
                INFORMATION INCLUDING WITHOUT LIMITATION YOUR EMAIL ADDRESS,
                NAME AND/OR CONTACT INFORMATION.
              </p>
            </div>

            <div className={styles.content}>
              <Heading size="sm" className={styles.sectionHeading}>
                Your Rights
              </Heading>
              <p>
                You have the following rights with respect to your personal
                information:
              </p>
              <ul className={styles.bulletList}>
                <li>
                  Right to Know: You have the right to request that we disclose
                  certain information to you about our collection, use, and
                  disclosure of your personal information over the past 12
                  months. Once we receive and confirm your request, we will
                  disclose the following to you:
                  <ul className={styles.subList}>
                    <li>
                      The categories of personal information we collected about
                      you.
                    </li>
                    <li>
                      The categories of sources for the personal information we
                      collected about you.
                    </li>
                    <li>
                      Our business or commercial purpose for collecting that
                      personal information.
                    </li>
                    <li>
                      The categories of third parties with whom we share that
                      personal information and the categories of personal
                      information shared with those third parties.
                    </li>
                    <li>
                      The specific pieces of personal information we collected
                      about you.
                    </li>
                  </ul>
                </li>
                <li>
                  Right to Opt Out: You have the right to opt-out of sharing of
                  your information with third parties, subject to certain
                  exceptions for SageSure to provide you products and services.
                </li>
                <li>
                  Right to Correction: You have the right to request correction
                  of any inaccurate personal information.
                </li>
                <li>
                  Right to Request Deletion: You have the right to request that
                  we delete your personal information that we have collected
                  about you, subject to certain exceptions.
                </li>
                <li>
                  Right to Non-Discrimination: We will not discriminate against
                  you because you exercise your rights. For example, we will not
                  deny you goods or services or charge you a different price or
                  rate for goods or services if you make an access or deletion
                  request.
                </li>
              </ul>
            </div>

            <div className={styles.content}>
              <Heading size="sm" className={styles.sectionHeading}>
                Exercising Your Rights
              </Heading>
              <p>
                To exercise the rights described above, please submit your
                request to us by either:
              </p>
              <ul className={styles.bulletList}>
                <li>Calling us at 1-800-481-0661; or</li>
                <li>
                  Emailing us at{" "}
                  <DSLink href="mailto:customercare@sagesure.com">
                    customercare@sagesure.com
                  </DSLink>
                </li>
              </ul>
              <p>
                Only you, or a person legally authorized to act on your behalf,
                may submit a request related to your personal information. You
                may also submit a request on behalf of your minor child. You may
                only make a request for information twice within a 12-month
                period. We will not charge a fee to respond to your request. Any
                such inquiry will be responded to within thirty-day (30) days.
                We must verify your identity with respect to such inquiries.
                Depending on the nature of the Personal Information at issue, we
                may require additional measures or information from you as part
                of that verification.
              </p>
              <p>
                You will be asked to provide your name, insured property
                address, and date of birth when making the request.
              </p>
            </div>

            <div className={styles.content}>
              <Heading size="sm" className={styles.sectionHeading}>
                Security
              </Heading>
              <p>
                SageSure implements reasonable security measures to ensure the
                security of your personal information. Please understand,
                however, that no data transmissions over the Internet can be
                guaranteed to be 100% secure. Consequently, SageSure cannot
                ensure or warrant the security of any information you transmit
                to us and you understand that any information that you transfer
                to us is done at your own risk. If we learn of a security system
                breach we may attempt to notify you so that you can take
                appropriate protective steps. By using the services or providing
                personal information to us, you agree that we can communicate
                with you electronically regarding security, privacy, and
                administrative issues relating to your use of the services. We
                may post a notice via our website if a security breach occurs.
                We may also send an email to you at the email address you have
                provided to us in these circumstances. Depending on where you
                live, you may have a legal right to receive notice of a security
                breach in writing.
              </p>
            </div>

            <div className={styles.content}>
              <Heading size="sm" className={styles.sectionHeading}>
                Data Retention
              </Heading>
              <p>
                We will not retain your personal information any longer than
                necessary to fulfill the purposes described in this our Privacy
                Policy. Under our record retention policy, we are required to
                destroy personal information after we no longer need it
                according to specific retention periods. However, we may need to
                hold personal information beyond these retention periods due to
                regulatory requirements or in response to a regulatory audit,
                investigation, or other legal matter.
              </p>
            </div>

            <div className={styles.content}>
              <Heading size="sm" className={styles.sectionHeading}>
                For More Information
              </Heading>
              <p>
                If you have any questions regarding this Privacy Notice, please
                visit us at{" "}
                <DSLink
                  href="https://www.sagesure.com/contact-us/"
                  target="_blank"
                  rel="noopener"
                >
                  www.sagesure.com/contact-us/
                </DSLink>
              </p>
              <p>
                Updates to these Policies: From time to time, we may modify or
                update these policies at our sole discretion. We encourage you
                to review these policies frequently for such changes. These
                policies were last updated July 1, 2023.
              </p>
            </div>
          </Card>
        </div>
      </div>
      <PageFooter unauthenticated />
    </>
  );
};

export default Privacy;
